/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { getZOrderColor } from '@/lib/map/GeofencesInView'

const LAYERS = [1, 2, 3, 4, 5]

/**
 * A geofence's display layer (z-order), 1-5: a segmented control in the layers' map colours, with
 * a stack of plates beside it showing where the chosen layer sits. Replaces Refresh's slider.
 * Without `onChange` it is read-only, for showing the layer outside the editor.
 */
interface ZOrderPickerProps {
  value: number
  onChange?: (value: number) => void
  readOnly?: boolean
}

export const ZOrderPicker = ({ value, onChange, readOnly = false }: ZOrderPickerProps) => (
  <div className="flex items-center gap-4">
    {!readOnly && (
      <div className="join" role="radiogroup" aria-label="Display layer">
        {LAYERS.map((layer) => {
          const selected = layer === value
          return (
            <button
              key={layer}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`Layer ${layer}`}
              onClick={() => onChange?.(layer)}
              className={`join-item btn btn-sm w-9 px-0 border-base-300 ${selected ? 'text-white' : 'btn-ghost'}`}
              style={
                selected ? { backgroundColor: getZOrderColor(layer), borderColor: getZOrderColor(layer) } : undefined
              }
            >
              {layer}
            </button>
          )
        })}
      </div>
    )}
    {/* Layer 5 on top. Purely illustrative, so hidden from screen readers. */}
    <svg width="64" height="54" viewBox="0 0 64 54" aria-hidden>
      {[...LAYERS].reverse().map((layer, index) => (
        <path
          key={layer}
          d="M12 0 L60 0 L52 10 L4 10 Z"
          transform={`translate(0 ${index * 10 + 2})`}
          fill={layer === value ? getZOrderColor(layer) : 'currentColor'}
          fillOpacity={layer === value ? 0.9 : 0.12}
          stroke={layer === value ? getZOrderColor(layer) : 'currentColor'}
          strokeOpacity={layer === value ? 1 : 0.3}
        />
      ))}
    </svg>
  </div>
)
